import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, message } from "antd";
import { SendOutlined, DownloadOutlined } from "@ant-design/icons";
import { useSystemConfig } from "../../Hook";
import { IMAGE_URL } from "../../Constant";
import {
  useSendQuotationWhatsAppMutation,
  useWhatsAppConnected,
} from "../../Services";
import { useQuotationDocument } from "./useQuotationDocument";
import { DRAWER_FORM_WIDTH, drawerTitle } from "../AppUi";
import { ResizableDrawer } from "../ResizableDrawer/ResizableDrawer";

interface QuotationDrawerProps {
  open: boolean;
  quotation: any | null;
  onClose: () => void;
}

export const QuotationDrawer: React.FC<QuotationDrawerProps> = ({
  open,
  quotation,
  onClose,
}) => {
  const {
    storeName,
    address,
    phone,
    currency,
    receiptFooter,
    appLogo,
    isWhatsAppEnabled,
  } = useSystemConfig();

  const config = {
    store_name: storeName,
    address,
    phone,
    currency,
    receipt_footer: receiptFooter,
    logo_url: appLogo ? `${IMAGE_URL}/${appLogo}` : "",
  };

  const {
    getQuotationHtml,
    printQuotation,
    downloadQuotation,
    getQuotationPdfBase64,
  } = useQuotationDocument({ quotation, config });

  const [whatsappPhone, setWhatsappPhone] = useState<string>("");
  const sendQuotationMutation = useSendQuotationWhatsAppMutation();
  const isWhatsAppConnected = true; //useWhatsAppConnected();

  useEffect(() => {
    if (quotation) {
      setWhatsappPhone((prev) => {
        if (prev.trim()) return prev;
        return String(
          quotation.customer_phone ||
            quotation.temp_customer_phone ||
            phone ||
            "",
        );
      });
    }
  }, [quotation, phone]);

  const handleSendWhatsApp = useCallback(async () => {
    if (!quotation || !whatsappPhone.trim()) {
      message.warning(
        "Enter the WhatsApp phone number to send the quotation to.",
      );
      return;
    }

    try {
      const pdfBase64 = await getQuotationPdfBase64();
      if (!pdfBase64) {
        message.error("Failed to generate the quotation PDF.");
        return;
      }

      const res = await sendQuotationMutation.mutateAsync({
        quotation_id: String(quotation.id),
        phone: whatsappPhone.trim(),
        pdf_base64: pdfBase64,
        filename: `Quotation_${String(
          quotation.quotation_name || quotation.id,
        )}.pdf`,
      });

      if (res?.success) {
        message.success("Quotation sent via WhatsApp!");
      } else {
        message.error(res?.message || "Failed to send the quotation.");
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to send the quotation.");
    }
  }, [getQuotationPdfBase64, quotation, whatsappPhone, sendQuotationMutation]);

  if (!quotation) return null;

  return (
    <ResizableDrawer
      open={open}
      onClose={onClose}
      title={drawerTitle("Quotation Preview")}
      width={DRAWER_FORM_WIDTH}
      className="quotation-drawer"
      destroyOnClose
      footer={
        <div className="flex flex-col gap-2">
          <div className="flex justify-end gap-2">
            <Button
              type="primary"
              size="middle"
              onClick={printQuotation}
              id="print_quotation_pdf"
              className="text-xs font-semibold h-9 rounded-md"
            >
              Print Quotation
            </Button>
            <Button
              size="middle"
              icon={<DownloadOutlined />}
              onClick={downloadQuotation}
              id="download_quotation_pdf"
              className="text-xs font-semibold h-9 rounded-md"
            >
              Download PDF
            </Button>
          </div>
          <div className="flex gap-2 items-center border-t border-gray-150 pt-2">
            <Input
              placeholder="WhatsApp number (e.g. 0712345678)"
              prefix={<SendOutlined className="text-gray-400" />}
              value={whatsappPhone}
              onChange={(e) => setWhatsappPhone(e.target.value)}
              onPressEnter={handleSendWhatsApp}
              className="h-9 text-xs"
              allowClear
            />
            <Button
              type="primary"
              size="middle"
              loading={sendQuotationMutation.isPending}
              onClick={handleSendWhatsApp}
              id="send_quotation_whatsapp"
              className="text-xs font-semibold h-9 rounded-md bg-green-600"
              disabled={!isWhatsAppEnabled || !isWhatsAppConnected}
            >
              Send Quotation
            </Button>
          </div>
        </div>
      }
    >
      <div
        dangerouslySetInnerHTML={{ __html: getQuotationHtml(false) }}
        style={{ width: "100%" }}
      />
    </ResizableDrawer>
  );
};
